import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ImageUpload } from '@/components/ImageUpload';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { Club } from '@/model';
import { usePutClubMutation } from '@/services/queries/club';
import { PutClubRequest } from '@/services/request/club';
import { useTranslation } from 'react-i18next';

export interface TeamsEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Club;
  onClose: () => void;
  onDone?: () => void;
  itemsData: Club[];
}

export const TeamsEditModal = ({ isOpen, onClose, item, onDone, itemsData }: TeamsEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutClubMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      name: item.name,
      acronym: item.acronym,
      file: undefined,
      is_favorite: item.favorite ?? false,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      if (data.is_favorite && itemsData.some(club => club.favorite && club.uuid !== item.uuid)) {
        toast({
          status: 'warning',
          title: t('pages.application_teams.components.teams_edit_modal.teams_edit_favorite_exists'),
        });
        return;
      }

      const payload: PutClubRequest = {
        image: data.file,
        name: data.name,
        acronym: data.acronym,
        is_favorite: data.is_favorite,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_teams.components.teams_edit_modal.teams_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_teams.components.teams_edit_modal.teams_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t, itemsData],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_teams.components.teams_edit_modal.teams_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <Flex direction='row' gap={4} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_teams.components.teams_edit_modal.teams_edit_name_label')}
                {...register('name')}
                helperText={t('pages.application_teams.components.teams_edit_modal.teams_edit_name_helper_text')}
                maxLength={100}
                rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
                flex='1'
              />

              <InputField
                withoutFloating
                label={t('pages.application_teams.components.teams_edit_modal.teams_edit_acronym_label')}
                {...register('acronym')}
                helperText={t('pages.application_teams.components.teams_edit_modal.teams_edit_acronym_helper_text')}
                maxLength={3}
                rootProps={{ isInvalid: !!errors?.acronym, errorMessage: errors?.acronym?.message }}
                flex='1'
              />
            </Flex>

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_teams.components.teams_edit_modal.teams_edit_shield_label')}
                imageURL={item.logo_path ?? ''}
                onFileChange={file => {
                  setValue('file', file);
                }}
                helpText={t('pages.application_teams.components.teams_edit_modal.teams_edit_shield_helper_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            <Flex direction='column' gap={1}>
              <Flex alignItems='center' gap={2}>
                <Switch {...register('is_favorite')} />
                <Text>{t('pages.application_teams.components.teams_edit_modal.teams_edit_favorite_label')}</Text>
              </Flex>
              <Text fontSize='sm' color='gray.500'>
                {t('pages.application_teams.components.teams_edit_modal.teams_edit_favorite_description')}
              </Text>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_teams.components.teams_edit_modal.teams_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>{t('pages.application_teams.components.teams_edit_modal.teams_edit_submit')}</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
